import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import PageSeo from "@/components/PageSeo";
import { projectPoster } from "@/lib/vimeo";
import { divisionColor } from "@/lib/divisions";
import { HoverVideo } from "@/components/VimeoEmbed";
import { ArrowUpRight } from "lucide-react";
import { SITE } from "@/constants/testIds";

function ProjectCard({ p, hovered, setHovered, feature = false }) {
  const aspect = p.aspect === "9:16" ? "9 / 16" : "16 / 9";
  const href = `/work/${p.slug}`;
  return (
    <motion.li
      id={p.slug}
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-10%" }}
      transition={{ duration: 0.5 }}
      onMouseEnter={() => setHovered(p.id)}
      onMouseLeave={() => setHovered(null)}
      className="group relative bg-charcoal overflow-hidden cursor-pointer block scroll-mt-32"
    >
      {/* Wrapping anchor: click / right-click / cmd-click all open the dedicated
          detail page at /work/{slug} — proper canonical URL, own OG image. */}
      <Link to={href} aria-label={`Open ${p.title}`} className="absolute inset-0 z-10" data-testid={`work-card-link-${p.slug}`} />
      <div className="relative w-full" style={{ aspectRatio: aspect }}>
        <img
          src={projectPoster(p)}
          alt={p.title}
          className="card-img absolute inset-0 w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            const fb = `https://vumbnail.com/${p.vimeo_id}_large.jpg`;
            if (e.currentTarget.src !== fb) e.currentTarget.src = fb;
            else e.currentTarget.style.display = "none";
          }}
        />
        <HoverVideo id={p.vimeo_id} active={hovered === p.id} />
        <div className="absolute bottom-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <ArrowUpRight className="w-5 h-5 text-bone" />
        </div>
      </div>
      <div className="p-5 flex items-center justify-between border-t border-line">
        <span className={`font-display tracking-tight truncate pr-4 text-bone ${feature ? "text-xl md:text-2xl" : "text-base md:text-lg"}`}>{p.title}</span>
        <span className="eyebrow shrink-0" style={{ color: divisionColor(p.division) }}>{p.division || "Studio"}</span>
      </div>
    </motion.li>
  );
}

export default function Work() {
  const [projects, setProjects] = useState([]);
  const [hovered, setHovered] = useState(null);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/projects").then((r) => setProjects(r.data || [])).catch(() => {});
  }, []);

  const filters = ["All", "Marketing", "Studio", "Experience"];
  const filtered = filter === "All" ? projects : projects.filter((p) => (p.division || "Studio") === filter);

  // Deep-link support: when opened via /work#{slug}, scroll to it and auto-preview.
  useEffect(() => {
    if (!projects.length) return;
    const raw = (typeof window !== "undefined" && window.location.hash) || "";
    const slug = decodeURIComponent(raw.replace(/^#/, ""));
    if (!slug) return;
    const match = projects.find((p) => p.slug === slug);
    if (!match) return;
    // If the target is filtered out, reset filter first so it renders.
    if (filter !== "All" && (match.division || "Studio") !== filter) {
      setFilter("All");
      return; // effect will re-run when projects list re-derives
    }
    // Wait one frame for React to paint the target element before scrolling.
    requestAnimationFrame(() => {
      const el = document.getElementById(slug);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      setHovered(match.id); // auto-play preview on arrival
    });
  }, [projects, filter]);

  // Curated editorial layout: top 2 as feature row (large), then masonry
  const feature = filtered.slice(0, 2);
  const rest = filtered.slice(2);

  // Build an ItemList of VideoObject + CreativeWork schemas so Google can
  // surface individual projects in the Video carousel and AI answer engines
  // can enumerate the portfolio without executing JS.
  const workJsonLd = projects.length > 0 ? {
    "@context": "https://schema.org",
    "@type": "ItemList",
    "name": "Relatable Studio · Selected Work",
    "numberOfItems": projects.length,
    "itemListElement": projects.slice(0, 60).map((p, idx) => ({
      "@type": "ListItem",
      "position": idx + 1,
      "item": {
        "@type": ["CreativeWork", "VideoObject"],
        "@id": `https://relatable.studio/work/${p.slug}`,
        "name": p.title,
        "url": `https://relatable.studio/work/${p.slug}`,
        "description": p.approach || p.challenge || p.outcome || `${p.title} — a ${p.division || "Studio"} film by Relatable Studio.`,
        "thumbnailUrl": projectPoster(p),
        "contentUrl": `https://vimeo.com/${p.vimeo_id}`,
        "embedUrl": `https://player.vimeo.com/video/${p.vimeo_id}`,
        "uploadDate": (p.created_at || "").slice(0, 10) || undefined,
        "genre": p.division || "Studio",
        "creator": { "@type": "Organization", "@id": "https://relatable.studio/#organization", "name": "Relatable Studio" },
        "publisher": { "@type": "Organization", "@id": "https://relatable.studio/#organization" },
        "inLanguage": "en",
        "isFamilyFriendly": true,
      }
    }))
  } : null;

  return (
    <main data-testid={SITE.workPage} className="bg-ink text-bone min-h-screen">
      <PageSeo
        title="Selected Work · Brand Films, Ad Campaigns & Documentaries · Relatable Studio"
        description="Selected work from Relatable Studio — brand films, ad campaigns, employer branding videos, founder stories, documentaries and corporate films produced across India. A curated working selection."
        path="/work"
        breadcrumbs={[
          { name: "Home", path: "/" },
          { name: "Work", path: "/work" },
        ]}
        jsonLd={workJsonLd}
      />
      <section className="pt-28 md:pt-32 px-6 md:px-12 lg:px-24">
        <p className="eyebrow text-fog mb-5">◐ Work · The Library</p>
        <h1 className="font-display text-6xl md:text-8xl lg:text-9xl tracking-tighter font-medium leading-[0.92]">
          Films, campaigns,<br/>
          <span className="text-relatable">conversations.</span>
        </h1>
        <p className="mt-8 max-w-2xl text-fog text-base md:text-lg leading-relaxed">
          A curated working selection. Hover any tile to preview the film. Vertical and horizontal formats are presented at their original aspect ratio.
        </p>

        <div className="mt-12 flex flex-wrap gap-2 border-t border-line pt-8">
          {filters.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`eyebrow px-4 py-2 border transition-colors ${
                filter === f ? "border-bone bg-bone text-ink" : "border-line text-fog hover:text-bone hover:border-bone"
              }`}
            >
              {f === "All" ? "All Work" : `Relatable ${f}`}
            </button>
          ))}
        </div>
      </section>

      {/* Feature row — 2 large cards */}
      {feature.length > 0 && (
        <div className="mt-12 md:mt-16 px-6 md:px-12 lg:px-24">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8">
            {feature.map((p) => (
              <ProjectCard key={p.id} p={p} hovered={hovered} setHovered={setHovered} feature />
            ))}
          </div>
        </div>
      )}

      {/* Editorial masonry — varied widths */}
      <div className="mt-12 md:mt-16 px-6 md:px-12 lg:px-24 pb-24">
        <ul className="columns-1 md:columns-2 lg:columns-3 gap-6 md:gap-8">
          {rest.map((p) => (
            <div key={p.id} className="mb-6 md:mb-8 break-inside-avoid">
              <ProjectCard p={p} hovered={hovered} setHovered={setHovered} />
            </div>
          ))}
        </ul>

        {filtered.length === 0 && (
          <p className="text-fog py-16 text-center eyebrow">No films in this division yet.</p>
        )}

        <div className="mt-20 text-center">
          <p className="eyebrow text-fog">— end of library —</p>
          <Link to="/contact" className="mt-6 inline-flex items-center gap-3 px-7 py-4 bg-bone text-ink eyebrow hover:bg-relatable hover:text-bone transition-colors">
            Have a project in mind? Start →
          </Link>
        </div>
      </div>
    </main>
  );
}
